
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer } from "recharts"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { TrendingUp, TrendingDown, BarChart3 } from "lucide-react"
import { useAuth } from "@/lib/auth/auth-context"
import { useAccountSelection } from "@/lib/context/account-selection-context"
import { useEffect, useState } from "react"
import {
  fetchMonthlyData,
  fetchCategoryData,
  fetchPeriodComparison,
  fetchWeeklyData,
  fetchYearlyData
} from "@/lib/services/statistics"
import type { MonthlyData, CategoryData } from "@/lib/types"
import type { StatisticsTotals } from "@/lib/services/statistics"
import { toast } from "sonner"
import { getCategoryIcon } from "@/lib/utils/category-helpers"
import { NotificationBell } from "@/components/notification-bell"

type TimePeriod = 'week' | 'month' | 'year'

interface StatisticsProps {
  onNavigate?: (view: string) => void
}

export function Statistics({ onNavigate }: StatisticsProps = {}) {
  const { user } = useAuth()
  const { selectedAccountId } = useAccountSelection()
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('month')
  const [loading, setLoading] = useState(true)
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([])
  const [categoryData, setCategoryData] = useState<CategoryData[]>([])
  const [totals, setTotals] = useState<StatisticsTotals>({
    totalBalance: 0,
    totalIncome: 0,
    totalExpenses: 0,
    monthOverMonthChange: 0,
    hasPastData: false
  })

  useEffect(() => {
    if (!user?.id) return

    const loadStatistics = async () => {
      try {
        setLoading(true)

        // Fetch data based on selected time period
        let chartData: MonthlyData[]
        if (timePeriod === 'week') {
          chartData = await fetchWeeklyData(user.id, 4, selectedAccountId)
        } else if (timePeriod === 'year') {
          chartData = await fetchYearlyData(user.id, 2, selectedAccountId)
        } else {
          chartData = await fetchMonthlyData(user.id, 5, selectedAccountId)
        }

        const [categoryStats, totalsStats] = await Promise.all([
          fetchCategoryData(user.id, 5, selectedAccountId),
          fetchPeriodComparison(user.id, timePeriod, selectedAccountId)
        ])

        setMonthlyData(chartData)
        setCategoryData(categoryStats)
        setTotals(totalsStats)
      } catch (error) {
        console.error('Error loading statistics:', error)
        toast.error('Failed to load statistics')
      } finally {
        setLoading(false)
      }
    }

    loadStatistics()
  }, [user?.id, timePeriod, selectedAccountId])

  const formatCurrency = (value: number) => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}k`
    }
    return `$${value}`
  }

  const getTimePeriodLabel = () => {
    if (timePeriod === 'week') return 'Last 4 weeks'
    if (timePeriod === 'year') return 'Last 2 years'
    return 'Last 5 months'
  }

  const getPeriodChangeLabel = () => {
    if (timePeriod === 'week') return 'from last week'
    if (timePeriod === 'year') return 'from last year'
    return 'from last month'
  }

  const getCategoryEmoji = (category: string, index: number): string => {
    const categoryIcons: { [key: string]: string } = {
      'Food': '🍔',
      'Transport': '🚗',
      'Shopping': '🛍️',
      'Entertainment': '🎬',
      'Health': '💪',
      'Utilities': '💡',
      'Education': '📚',
      'Travel': '✈️',
      'Other': '📦'
    }
    return categoryIcons[category] || ['🍔', '🚗', '🛍️', '🎬', '💪', '💡', '📚', '✈️'][index] || '📦'
  }

  if (loading) {
    return (
      <div className="space-y-6 p-4 pb-24">
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-center h-96">
            <p className="text-muted-foreground">Loading statistics...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 pb-24">
      <div className="max-w-md mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Statistics</h1>
          <p className="text-sm text-muted-foreground mt-1">Analyze your financial trends</p>
        </div>
        <NotificationBell />
      </div>

      {/* Time Period Selection */}
      <div className="flex gap-1.5 sm:gap-2">
        <Button
          variant={timePeriod === 'week' ? 'default' : 'outline'}
          size="sm"
          className="text-xs sm:text-sm px-2 sm:px-3"
          onClick={() => setTimePeriod('week')}
        >
          Week
        </Button>
        <Button
          variant={timePeriod === 'month' ? 'default' : 'outline'}
          size="sm"
          className="text-xs sm:text-sm px-2 sm:px-3"
          onClick={() => setTimePeriod('month')}
        >
          Month
        </Button>
        <Button
          variant={timePeriod === 'year' ? 'default' : 'outline'}
          size="sm"
          className="text-xs sm:text-sm px-2 sm:px-3"
          onClick={() => setTimePeriod('year')}
        >
          Year
        </Button>
      </div>

      {/* Total Balance Card */}
      <Card className="p-4 sm:p-6">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Total Balance</p>
          <h2 className="text-3xl sm:text-4xl font-bold">${totals.totalBalance.toFixed(2)}</h2>
          {totals.hasPastData && (
            <div className={`flex items-center gap-2 ${totals.monthOverMonthChange >= 0 ? 'text-income' : 'text-expense'}`}>
              {totals.monthOverMonthChange >= 0 ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              <span className="text-sm font-medium">
                {totals.monthOverMonthChange >= 0 ? '+' : ''}{totals.monthOverMonthChange.toFixed(1)}% {getPeriodChangeLabel()}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Income vs Expenses Chart */}
      <Card className="p-4 sm:p-6">
        <div className="space-y-4">
          <div className="space-y-1">
            <h3 className="text-base sm:text-lg font-semibold">Income vs Expenses</h3>
            <p className="text-xs sm:text-sm text-muted-foreground">{getTimePeriodLabel()} overview</p>
          </div>

          {monthlyData.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No data available for this period</p>
            </div>
          ) : (
            <div className="w-full -mx-2 sm:mx-0">
              <ChartContainer
                config={{
                  income: {
                    label: "Income",
                    color: "var(--income)",
                  },
                  expenses: {
                    label: "Expenses",
                    color: "var(--expense)",
                  },
                }}
                className="h-[280px] sm:h-[320px] md:h-[350px] w-full"
              >
                <AreaChart
                  data={monthlyData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <defs>
                    <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--income)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--income)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--expense)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--expense)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} vertical={false} />
                  <XAxis
                    dataKey="month"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 12,
                    }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                    dy={8}
                  />
                  <YAxis
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 11,
                    }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={formatCurrency}
                    width={45}
                    dx={-5}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent formatter={(value) => `$${value}`} labelFormatter={(label) => `${label}`} />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="var(--income)"
                    fillOpacity={1}
                    fill="url(#colorIncome)"
                    strokeWidth={2.5}
                    dot={{
                      fill: "var(--income)",
                      strokeWidth: 2,
                      r: 4,
                      stroke: "hsl(var(--background))",
                    }}
                    activeDot={{ r: 6 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="expenses"
                    stroke="var(--expense)"
                    fillOpacity={1}
                    fill="url(#colorExpenses)"
                    strokeWidth={2.5}
                    dot={{
                      fill: "var(--expense)",
                      strokeWidth: 2,
                      r: 4,
                      stroke: "hsl(var(--background))",
                    }}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ChartContainer>
          </div>
          )}

          {monthlyData.length > 0 && (
            <div className="flex items-center justify-center gap-6 pt-2">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-income" />
              <span className="text-xs sm:text-sm text-muted-foreground">Income</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-expense" />
              <span className="text-xs sm:text-sm text-muted-foreground">Expenses</span>
            </div>
          </div>
          )}
        </div>
      </Card>

      {/* Category Details */}
      <Card className="p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-4">Category Details</h3>
        {categoryData.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No expense data for this month</p>
          </div>
        ) : (
          <div className="space-y-4">
            {(() => {
              const totalExpenses = categoryData.reduce((sum, cat) => sum + cat.amount, 0)

              return categoryData.map((category, index) => {
                const percentage = totalExpenses > 0 ? (category.amount / totalExpenses) * 100 : 0

                return (
                  <div key={index} className="flex items-center gap-3 sm:gap-4">
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center text-lg shrink-0"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      {getCategoryEmoji(category.category, index)}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium text-sm sm:text-base truncate">{category.category}</span>
                        <span className="font-semibold text-sm sm:text-base shrink-0">${category.amount.toFixed(2)}</span>
                      </div>
                      <div className="space-y-1">
                        <Progress
                          value={percentage}
                          className="h-2"
                          indicatorStyle={{ backgroundColor: category.color }}
                        />
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{percentage.toFixed(1)}% of total</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            })()}
          </div>
        )}
      </Card>
      </div>
    </div>
  )
}
